import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../data";

function GalleryItem({ item, index, onSelect }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // alternate parallax direction/intensity for editorial feel
  const dir = index % 2 === 0 ? -1 : 1;
  const y = useTransform(scrollYProgress, [0, 1], [40 * dir, -40 * dir]);

  return (
    <motion.figure
      ref={ref}
      onClick={onSelect}
      className={`group relative overflow-hidden cursor-zoom-in ${
        item.tall ? "row-span-2 h-[560px] md:h-[720px]" : "h-[340px] md:h-[420px]"
      }`}
      data-testid={`gallery-item-${index}`}
    >
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0)" }}
        whileInView={{ clipPath: "inset(0% 0 0 0)" }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="h-full w-full overflow-hidden relative"
      >
        <motion.div
          style={{ y }}
          className="absolute inset-x-0 -top-[10%] h-[120%] w-full"
        >
          <img
            src={item.url}
            alt={item.caption}
            className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          />
        </motion.div>
      </motion.div>
      <figcaption className="absolute bottom-4 left-4 text-xs tracking-[0.2em] uppercase text-pearl opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-difference z-10">
        {item.caption}
      </figcaption>
    </motion.figure>
  );
}

// Custom hook to handle scroll logic (framer-motion hook needs custom imports)
import { useScroll, useTransform } from "framer-motion";

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    if (activeIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeIndex]);

  useEffect(() => {
    if (activeIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setActiveIndex(null);
      if (e.key === "ArrowLeft") {
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : GALLERY.length - 1));
      }
      if (e.key === "ArrowRight") {
        setActiveIndex((prev) => (prev < GALLERY.length - 1 ? prev + 1 : 0));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex]);

  return (
    <section id="portfolio" className="bg-pearl py-24 md:py-36 overflow-visible" data-testid="portfolio">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-end justify-between mb-14 md:mb-20">
          <h2 className="font-serif font-light text-5xl md:text-7xl tracking-tight leading-none">
            Das <span className="italic text-stone">Portfolio</span>
          </h2>
          <p className="hidden md:block text-xs tracking-[0.25em] uppercase text-muted mb-2">
            Ausgewählte Aufnahmen
          </p>
        </div>

        <div className="border-l-[24px] sm:border-l-[30px] md:border-l-[45px] border-b-[32px] sm:border-b-[48px] md:border-b-[72px] border-charcoal pl-4 sm:pl-6 md:pl-10 pb-4 sm:pb-6 md:pb-10 relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 auto-rows-min">
            {GALLERY.map((item, i) => (
              <GalleryItem key={i} item={item} index={i} onSelect={() => setActiveIndex(i)} />
            ))}
          </div>

          {/* firmagallery.png signature */}
          <img
            src="/img/firmagallery.png"
            alt="Signature"
            className="sm:absolute bottom-12 sm:bottom-20 md:bottom-32 lg:bottom-44 left-[10%] sm:left-[35%] md:left-[35%] h-16 sm:h-20 md:h-28 lg:h-36 w-auto object-contain z-10 mx-auto mt-8 sm:mt-0"
          />

          {/* Janna sitting overlay (fondogalerry.png) */}
          <img
            src="/img/fondogalerry.png"
            alt="Janna sitting"
            className="hidden sm:block sm:absolute right-0 md:right-0 bottom-[-36px] md:bottom-[-217px] h-auto sm:h-[440px] md:h-[580px] lg:h-[850px] w-auto object-contain z-20"
          />
        </div>
      </div>

      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActiveIndex(null)}
            className="fixed inset-0 z-[20000] bg-charcoal/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          >
            {/* Close Button */}
            <button
              onClick={() => setActiveIndex(null)}
              className="absolute top-6 right-6 text-pearl/60 hover:text-pearl transition-colors duration-300 z-50 p-2 rounded-full hover:bg-pearl/5"
              aria-label="Schließen"
            >
              <X className="w-8 h-8" strokeWidth={1.5} />
            </button>

            {/* Navigation Left */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex((prev) => (prev > 0 ? prev - 1 : GALLERY.length - 1));
              }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-pearl/50 hover:text-pearl transition-all duration-300 p-3 bg-pearl/5 hover:bg-pearl/10 rounded-full hover:scale-105 cursor-pointer z-50"
              aria-label="Vorheriges Bild"
            >
              <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" strokeWidth={1.5} />
            </button>

            {/* Large Image */}
            <div className="relative max-h-[80vh] max-w-[90vw] md:max-h-[85vh] md:max-w-[80vw] flex items-center justify-center overflow-hidden">
              <motion.img
                key={activeIndex}
                src={GALLERY[activeIndex].url}
                alt=""
                initial={{ opacity: 0, scale: 0.97, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 10 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="max-h-[80vh] max-w-[90vw] md:max-h-[85vh] md:max-w-[80vw] object-contain select-none shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Navigation Right */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex((prev) => (prev < GALLERY.length - 1 ? prev + 1 : 0));
              }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-pearl/50 hover:text-pearl transition-all duration-300 p-3 bg-pearl/5 hover:bg-pearl/10 rounded-full hover:scale-105 cursor-pointer z-50"
              aria-label="Nächstes Bild"
            >
              <ChevronRight className="w-6 h-6 md:w-8 md:h-8" strokeWidth={1.5} />
            </button>

            {/* Image Counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-pearl/50 text-[10px] tracking-[0.25em] uppercase select-none z-50">
              {activeIndex + 1} / {GALLERY.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
